import React, { useState, useMemo } from 'react';
import { leaderApi } from '../../../../api/leaderApi';
import KolonnenManagementModal from './KolonnenManagementModal';
import BulkActionBar from '../../../../components/shared/BulkActionBar';
import ActionMenu from '../../../../components/shared/ActionMenu/ActionMenu';
import './KolonnenView.css';

/**
 * @file KolonnenView.jsx
 * @description View for managing columns (teams) in Employee Management.
 */
export default function KolonnenView({ 
    kolonnen = [], 
    workers = [], 
    vehicles = [], 
    onUpdate,
    isModalOpen,
    setIsModalOpen,
    currentKolonne,
    setCurrentKolonne,
    viewMode = 'grid',
    selectedIds = [],
    setSelectedIds
}) {
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedKolonnen = useMemo(() => {
        return [...kolonnen].sort((a, b) => {
            if (sortConfig.key === 'name' || sortConfig.key === 'code') {
                let aVal = a[sortConfig.key] || '';
                let bVal = b[sortConfig.key] || '';
                return sortConfig.direction === 'asc' 
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            } else if (sortConfig.key === 'workers') {
                const aCount = a.members?.filter(m => m.type === 'worker').length || 0;
                const bCount = b.members?.filter(m => m.type === 'worker').length || 0;
                return sortConfig.direction === 'asc' ? aCount - bCount : bCount - aCount;
            } else if (sortConfig.key === 'vehicles') {
                const aCount = a.members?.filter(m => m.type === 'vehicle').length || 0;
                const bCount = b.members?.filter(m => m.type === 'vehicle').length || 0;
                return sortConfig.direction === 'asc' ? aCount - bCount : bCount - aCount;
            }
            return 0;
        });
    }, [kolonnen, sortConfig]);

    const handleEdit = (k) => {
        setCurrentKolonne(k);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Möchtest du diese Kolonne wirklich unwiderruflich löschen?')) {
            try {
                await leaderApi.deleteKolonne(id);
                if (onUpdate) onUpdate();
            } catch (err) {
                console.error('Error deleting kolonne:', err);
            }
        }
    };

    const handleBulkDelete = async () => {
        if (window.confirm(`Möchtest du diese ${selectedIds.length} Kolonnen wirklich unwiderruflich löschen?`)) {
            try {
                await Promise.all(selectedIds.map(id => leaderApi.deleteKolonne(id)));
                setSelectedIds([]);
                if (onUpdate) onUpdate();
            } catch (err) {
                console.error('Error deleting kolonnen:', err);
            }
        }
    };

    return (
        <div className="kolonnen-view">
            <BulkActionBar 
                selectedCount={selectedIds.length}
                onClearSelection={() => setSelectedIds([])}
                hideSelectionText={true}
                actions={[
                    { 
                        label: `${selectedIds.length} Löschen`, 
                        icon: '🗑️', 
                        variant: 'danger', 
                        onClick: handleBulkDelete 
                    }
                ]}
            />
            {viewMode === 'grid' ? (
                <div className="kolonnen-grid">
                    {kolonnen.map(k => (
                        <div key={k.id} className="kolonne-card" onClick={() => handleEdit(k)} style={{ cursor: 'pointer' }}>
                            <div className="kolonne-card-header">
                                <h3>{k.name}</h3>
                                <span className="kolonne-code">{k.code}</span>
                            </div>
                            <div className="kolonne-card-content">
                                    {(() => {
                                        const workersCount = k.members?.filter(m => m.type === 'worker').length || 0;
                                        const vehiclesCount = k.members?.filter(m => m.type === 'vehicle').length || 0;
                                        return (
                                            <div className="kolonne-stats">
                                                <div className="stat-item">
                                                    <span className="stat-icon">👥</span>
                                                    <span className="stat-label">Mitarbeiter:</span>
                                                    <span className="stat-count">{workersCount}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-icon">🚗</span>
                                                    <span className="stat-label">Fahrzeuge:</span>
                                                    <span className="stat-count">{vehiclesCount}</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="kolonne-card-actions">
                                    <button className="btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); handleEdit(k); }}>Bearbeiten</button>
                                    <button className="btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleDelete(k.id); }}>Löschen</button>
                                </div>
                            </div>
                        ))}
                        {kolonnen.length === 0 && (
                            <div className="kolonnen-empty">
                                <p>Keine Kolonnen gefunden. Lege eine neue an!</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="kolonnen-list-view">
                        <table className="kolonnen-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={selectedIds.length === kolonnen.length && kolonnen.length > 0}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedIds(kolonnen.map(k => k.id));
                                                } else {
                                                    setSelectedIds([]);
                                                }
                                            }}
                                        />
                                    </th>
                                    <th onClick={() => handleSort('code')} className={sortConfig.key === 'code' ? 'active' : ''}>
                                        Kennung {sortConfig.key === 'code' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th onClick={() => handleSort('name')} className={sortConfig.key === 'name' ? 'active' : ''}>
                                        Bezeichnung {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th onClick={() => handleSort('workers')} className={sortConfig.key === 'workers' ? 'active' : ''}>
                                        Mitarbeiter {sortConfig.key === 'workers' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th onClick={() => handleSort('vehicles')} className={sortConfig.key === 'vehicles' ? 'active' : ''}>
                                        Fahrzeuge {sortConfig.key === 'vehicles' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th style={{ width: '60px', textAlign: 'center' }}>Aktionen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedKolonnen.map(k => {
                                    const workerNames = k.members?.filter(m => m.type === 'worker')
                                        .map(m => workers.find(w => w.id === m.resource_id)?.name)
                                        .filter(Boolean) || [];
                                    
                                    const vehiclePlates = k.members?.filter(m => m.type === 'vehicle')
                                        .map(m => vehicles.find(v => v.id === m.resource_id)?.license_plate)
                                        .filter(Boolean) || [];

                                    const actions = [
                                        {
                                            icon: (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                            ),
                                            label: 'Bearbeiten',
                                            onClick: () => handleEdit(k),
                                            tooltip: 'Bearbeiten'
                                        },
                                        {
                                            icon: (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                </svg>
                                            ),
                                            label: 'Löschen',
                                            variant: 'delete',
                                            onClick: () => handleDelete(k.id),
                                            tooltip: 'Löschen'
                                        }
                                    ];

                                    return (
                                        <tr key={k.id} onClick={() => handleEdit(k)} style={{ cursor: 'pointer' }}>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedIds.includes(k.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedIds([...selectedIds, k.id]);
                                                        } else {
                                                            setSelectedIds(selectedIds.filter(id => id !== k.id));
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td style={{ fontWeight: '600', color: '#007aff' }}>{k.code || '—'}</td>
                                            <td style={{ fontWeight: '500' }}>{k.name}</td>
                                            <td>
                                                <div className="resource-pills-container">
                                                    {workerNames.slice(0, 2).map((name, i) => (
                                                        <span key={i} className="resource-pill">{name}</span>
                                                    ))}
                                                    {workerNames.length > 2 && (
                                                        <span className="resource-pill more">+{workerNames.length - 2}</span>
                                                    )}
                                                    {workerNames.length === 0 && <span style={{ color: '#86868b' }}>—</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="resource-pills-container">
                                                    {vehiclePlates.slice(0, 2).map((plate, i) => (
                                                        <span key={i} className="resource-pill">{plate}</span>
                                                    ))}
                                                    {vehiclePlates.length > 2 && (
                                                        <span className="resource-pill more">+{vehiclePlates.length - 2}</span>
                                                    )}
                                                    {vehiclePlates.length === 0 && <span style={{ color: '#86868b' }}>—</span>}
                                                </div>
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                                                <ActionMenu actions={actions} />
                                            </td>
                                        </tr>
                                    );
                                })}
                                {kolonnen.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center' }}>Keine Kolonnen gefunden.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

            {/* MODAL */}
            <KolonnenManagementModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                kolonne={currentKolonne}
                onKolonnenChange={onUpdate}
                allWorkers={workers}
                allVehicles={vehicles}
            />
        </div>
    );
}
